import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ChoiceChips from "@/components/ChoiceChips";
import EmptyData from "@/components/EmptyData";
import StatusBadge from "@/components/StatusBadge";

import AddIcon from "@mui/icons-material/Add";

import {PlayerAPI} from "@/api/player";
import {ERROR_OPERATION} from "@/utils/constants";
import {isPlayer} from "@/utils/access";

import "./index.scss";

const FIELD_DATE_FORMAT = "YYYY-MM-DD";

const PERIODS = [
	{value: 1, label: "Oggi"},
	{value: 7, label: "7 giorni"},
	{value: 14, label: "14 giorni"},
	{value: 30, label: "30 giorni"},
];

// tone: critical / warning / good per colorare il valore
const METRICS = {
	rpe: {
		label: "Fatica (RPE)",
		hint: "Fatica percepita dopo allenamento o gara, da 1 a 10.",
		dateKey: "rpeDate",
		valueKey: "rpe",
		load: PlayerAPI.listRPE,
		tone: (n) => (n >= 8 ? "critical" : n >= 5 ? "warning" : "good"),
		alertLabel: "Fatica alta",
	},
	tqr: {
		label: "Recupero (TQR)",
		hint: "Recupero percepito prima dell'allenamento, da 6 a 20.",
		dateKey: "tqrDate",
		valueKey: "tqrValue",
		load: PlayerAPI.listTQR,
		tone: (n) => (n <= 10 ? "critical" : n <= 14 ? "warning" : "good"),
		alertLabel: "Recupero scarso",
	},
	vas: {
		label: "Dolore (VAS)",
		hint: "Dolore segnalato dal giocatore, da 0 a 10.",
		dateKey: "vasDate",
		valueKey: "vasValue",
		load: PlayerAPI.listVAS,
		tone: (n) => (n >= 4 ? "critical" : n >= 1 ? "warning" : "good"),
		alertLabel: "Dolore segnalato",
	},
};

const METRIC_OPTIONS = Object.entries(METRICS).map(([value, m]) => ({
	value,
	label: m.label,
}));

function WellnessPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const user = useSelector((state) => state?.user);
	const player = isPlayer(user);

	const [periodDays, setPeriodDays] = useState(7);
	const [activeMetric, setActiveMetric] = useState("rpe");
	const [rawData, setRawData] = useState({rpe: [], tqr: [], vas: []});
	const [loading, setLoading] = useState(false);

	const dates = useMemo(() => {
		const result = [];
		for (let i = periodDays - 1; i >= 0; i--) {
			result.push(moment().subtract(i, "days").format(FIELD_DATE_FORMAT));
		}
		return result;
	}, [periodDays]);

	useEffect(() => {
		const fetchAll = async () => {
			setLoading(true);
			const params = {
				fromDate: dates[0],
				toDate: dates[dates.length - 1],
				...(player ? {playerId: user?.playerId} : {}),
			};
			const keys = Object.keys(METRICS);
			// Ogni misura si carica da sola: se una non risponde le altre restano
			const results = await Promise.allSettled(
				keys.map((k) => METRICS[k].load(params)),
			);
			const next = {};
			results.forEach((r, i) => {
				next[keys[i]] = r.status === "fulfilled" ? r.value || [] : [];
			});
			if (results.some((r) => r.status === "rejected"))
				handleNotification(ERROR_OPERATION, "error");
			setRawData(next);
			setLoading(false);
		};
		fetchAll();
	}, [dates]);

	const metric = METRICS[activeMetric];

	const players = useMemo(() => {
		const map = {};
		rawData[activeMetric].forEach((entry) => {
			const id = entry.playerId;
			const name = entry.player
				? entry.player.firstName + " " + entry.player.lastName
				: "---";
			const date = moment(entry[metric.dateKey]).format(FIELD_DATE_FORMAT);
			if (!map[id]) map[id] = {id, name, dates: {}};
			map[id].dates[date] = parseFloat(entry[metric.valueKey]);
		});
		return Object.values(map)
			.map((p) => {
				const values = Object.values(p.dates).filter((v) => !isNaN(v));
				const avg = values.length
					? values.reduce((s, v) => s + v, 0) / values.length
					: null;
				return {...p, avg};
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [rawData, activeMetric]);

	const alerts = players.filter((p) =>
		Object.values(p.dates).some((v) => metric.tone(v) === "critical"),
	);

	const cell = (value, extra = "") =>
		value === undefined || value === null || isNaN(value) ? (
			<span className={"wellness__cell wellness__cell--empty " + extra} />
		) : (
			<span
				className={
					"wellness__cell wellness__cell--" + metric.tone(value) + " " + extra
				}
			>
				{Number.isInteger(value) ? value : value.toFixed(1)}
			</span>
		);

	const mainActions = [
		{
			icon: <AddIcon />,
			title: "Inserisci recuperi (TQR)",
			onClick: () => navigate("/measurations/tqr/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Benessere"
				actions={!player && <MainActions actions={mainActions} />}
			/>
			<div className="wellness">
				<div className="wellness__filters">
					<ChoiceChips
						options={METRIC_OPTIONS}
						value={activeMetric}
						onChange={setActiveMetric}
					/>
					<ChoiceChips
						options={PERIODS}
						value={periodDays}
						onChange={setPeriodDays}
					/>
				</div>
				<div className="wellness__hint">{metric.hint}</div>

				{alerts.length > 0 && (
					<div className="wellness__alerts">
						<span className="wellness__alerts-label">{metric.alertLabel}:</span>
						{alerts.map((p) => (
							<StatusBadge key={p.id} tone="critical" label={p.name} />
						))}
					</div>
				)}

				{!loading && !players.length ? (
					<EmptyData>Nessun valore inserito nel periodo.</EmptyData>
				) : (
					<VLFCard
						content={
							<div className="wellness__table-wrap">
								<table className="wellness__table">
									<thead>
										<tr>
											<th className="wellness__name">Giocatore</th>
											{dates.map((d) => (
												<th key={d}>
													<span className="wellness__day">
														{moment(d).format("D")}
													</span>
													<span className="wellness__month">
														{moment(d).format("MMM")}
													</span>
												</th>
											))}
											<th>Media</th>
										</tr>
									</thead>
									<tbody>
										{players.map((p) => (
											<tr key={p.id}>
												<td className="wellness__name">{p.name}</td>
												{dates.map((d) => (
													<td key={d}>{cell(p.dates[d])}</td>
												))}
												<td>{cell(p.avg, "wellness__cell--avg")}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						}
					/>
				)}
			</div>
		</Page>
	);
}

export default WellnessPage;
